import React from "react";
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Send as SendIcon,
  Language as WebIcon,
  StopCircle as StopIcon,
} from "@mui/icons-material";
import { LANG, COMPOSER_MAX_W } from "./constants";

const Composer = ({
  inputRef,
  input,
  setInput,
  isStreaming,
  webAccess,
  setWebAccess,
  handleSend,
  abortRef,
  lang,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 1.25, sm: 2 },
        pt: 1.25,
        bgcolor: "background.default",
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
      }}
    >
      <Box
        sx={{
          maxWidth: COMPOSER_MAX_W,
          width: "100%",
          mx: "auto",
          bgcolor: "background.paper",
          border: `1.5px solid ${theme.palette.divider}`,
          borderRadius: { xs: 2.5, sm: 3.5 },
          overflow: "hidden",
          boxShadow: isDark
            ? `0 4px 24px ${alpha(theme.palette.common.black, 0.3)}`
            : `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}, ${
              isDark
                ? `0 4px 24px ${alpha(theme.palette.common.black, 0.3)}`
                : `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`
            }`,
          },
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={8}
          placeholder={lang?.typeMessage || LANG.typeMessage}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={isStreaming}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            "& .MuiInputBase-root": {
              px: { xs: 1.5, sm: 2.5 },
              pt: { xs: 1.25, sm: 1.75 },
              pb: 0.5,
              bgcolor: "transparent",
              fontSize: { xs: "0.9rem", sm: "0.9375rem" },
              lineHeight: 1.65,
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
              "&::placeholder": { color: "text.disabled", opacity: 1 },
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: { xs: 1, sm: 1.75 },
            pb: { xs: 1, sm: 1.25 },
            pt: 0.5,
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Tooltip title={lang?.webAccess || LANG.webAccess}>
            <Box
              onClick={() => setWebAccess((p) => !p)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                cursor: "pointer",
                border: `1px solid ${
                  webAccess
                    ? alpha(theme.palette.primary.main, 0.4)
                    : "transparent"
                }`,
                bgcolor: webAccess
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.text.primary, 0.04),
                transition: "all 0.15s",
                flexShrink: 0,
                "&:hover": {
                  bgcolor: webAccess
                    ? alpha(theme.palette.primary.main, 0.14)
                    : alpha(theme.palette.text.primary, 0.07),
                },
              }}
            >
              <WebIcon
                sx={{
                  fontSize: 14,
                  color: webAccess ? "primary.main" : "text.disabled",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: webAccess ? "primary.main" : "text.disabled",
                  lineHeight: 1,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {lang?.webAccess || LANG.webAccess}
              </Typography>
            </Box>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          {!isStreaming && (
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "text.disabled",
                display: { xs: "none", md: "block" },
                whiteSpace: "nowrap",
              }}
            >
              Enter to send · Shift+Enter for newline
            </Typography>
          )}

          {isStreaming ? (
            <IconButton
              size="small"
              onClick={() => {
                abortRef.current = true;
              }}
              sx={{
                bgcolor: "error.main",
                color: "error.contrastText",
                width: 34,
                height: 34,
                borderRadius: 2,
                flexShrink: 0,
                "&:hover": { bgcolor: "error.dark" },
                boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`,
              }}
            >
              <StopIcon sx={{ fontSize: 17 }} />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={handleSend}
              disabled={!input.trim()}
              sx={{
                background: input.trim()
                  ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                  : alpha(theme.palette.text.primary, 0.06),
                color: input.trim()
                  ? theme.palette.primary.contrastText
                  : alpha(theme.palette.text.primary, 0.25),
                width: 34,
                height: 34,
                borderRadius: 2,
                flexShrink: 0,
                boxShadow: input.trim()
                  ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.4)}`
                  : "none",
                "&:hover": {
                  background: input.trim()
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                    : undefined,
                  transform: input.trim() ? "scale(1.05)" : undefined,
                },
                transition: "all 0.15s",
              }}
            >
              <SendIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 1,
          color: "text.disabled",
          fontSize: "0.65rem",
          px: 1,
        }}
      >
        {lang?.chatDisclaimer ||
          "AI can make mistakes. Verify important information."}
      </Typography>
    </Box>
  );
};

export default Composer;
